import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Link, Stack, TextField, Typography } from '@mui/material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { auth } from './firebaseConfig';
import Brand from './components/Brand';
import GoogleButton from '../auth/GoogleButton';
import LanguageToggle from '../i18n/LanguageToggle';
import { useT } from '../i18n/LanguageContext';

const errorKey = (code = '') => {
  if (code.includes('email-already-in-use')) return 'auth.errInUse';
  if (code.includes('weak-password')) return 'auth.errWeak';
  if (code.includes('invalid-email')) return 'auth.errEmail';
  if (code.includes('network-request-failed')) return 'auth.errNetwork';
  return 'auth.errSignUp';
};

/**
 * Self sign-up, phone first: Google in one tap, or email + password. No SMS codes (Firebase bills
 * those per text). Creating the Firebase account is all this does — the app then sees a signed-in
 * user with no account and runs onboarding (business type, trucks, trial).
 */
export default function SignUp() {
  const t = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Into the app; App.js shows onboarding for a user with no account yet.
  const done = () => navigate('/ScanTicket');

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError(t('auth.errEmail')); return; }
    if (password.length < 6) { setError(t('auth.errWeak')); return; }
    setBusy(true);
    try {
      await createUserWithEmailAndPassword(auth, email.trim(), password);
      done();
    } catch (err) {
      setError(t(errorKey(err?.code)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F6F8FB' }}>
      <Box sx={{ bgcolor: '#0C447C', px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ maxWidth: 420, mx: 'auto' }}>
          <Brand nameColor="#fff" fontSize={16} />
          <LanguageToggle onDark />
        </Stack>
      </Box>
      <Box sx={{ maxWidth: 420, mx: 'auto', px: 2, py: 4 }}>
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, color: '#17202A' }}>{t('auth.signUpTitle')}</Typography>
        <Typography sx={{ fontSize: 15, color: '#596574', mt: 0.5, mb: 3 }}>{t('auth.signUpSub')}</Typography>

        <GoogleButton onDone={done} onError={setError} disabled={busy} />

        <Divider sx={{ my: 2.5, fontSize: 13, color: '#8A97A4' }}>{t('auth.or')}</Divider>

        <Box component="form" onSubmit={handleSignUp} noValidate>
          <Stack spacing={2}>
            <TextField label={t('auth.email')} type="email" fullWidth size="medium" autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
            <TextField label={t('auth.password')} type="password" fullWidth size="medium" autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} helperText={t('auth.passwordHelp')} />
          </Stack>
          <Button type="submit" fullWidth variant="contained" disableElevation disabled={busy}
            sx={{ mt: 2.5, height: 48, borderRadius: 2, fontSize: 16, fontWeight: 600, textTransform: 'none', bgcolor: '#0C447C' }}>
            {busy ? <CircularProgress size={22} color="inherit" /> : t('auth.createAccount')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: 14, color: '#596574' }}>
          {t('auth.haveAccount')}{' '}
          <Link component={RouterLink} to="/login" underline="hover">{t('auth.signIn')}</Link>
        </Typography>
        <Typography sx={{ mt: 1, textAlign: 'center', fontSize: 13, color: '#8A97A4' }}>{t('auth.trialLine')}</Typography>
      </Box>
    </Box>
  );
}
